import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Link,
  Typography,
  Button,
  Alert,
  AlertTitle,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";

// Icons
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";

// Components
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

// Redux & Helpers
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  loginUser,
  resendVerificationEmail,
  resetAuthServerStatus,
  clearUserInfoError,
} from "../redux/actions/authActions";
import { cleanFormData } from "../helpers/formHelpers";

// Types
import { UserLoginDto } from "../shared/dtos/auth/UserLoginDto";

interface ResendVerificationValues {
  companyCode: string;
  email: string;
}

const LoginForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const [resendModalOpen, setResendModalOpen] = useState(false);

  const { company } = useAppSelector((state) => state.company);
  const { loadingUserInfo, errorLoadingUserInfo, serverStatus } =
    useAppSelector((state) => state.auth);

  // 1. Validation Schemas
  const loginSchema = Yup.object().shape({
    username: Yup.string()
      .test("username-format", "Invalid format", function (value) {
        if (!value) return false;
        if (!company) {
          const fullFormatRegex = /^\d+-.+$/;
          if (!fullFormatRegex.test(value)) {
            return this.createError({
              message: "Enter full username (e.g., 01-Walter)",
            });
          }
        }
        const nameRegex = /^[A-Za-z0-9._-]+$/;
        const fullFormatRegex = /^\d+-.+$/;
        return nameRegex.test(value) || fullFormatRegex.test(value);
      })
      .required("Username is required"),
    password: Yup.string().min(4, "Min 4 characters").required("Required"),
  });

  const resendSchema = Yup.object().shape({
    companyCode: Yup.string().required("Required"),
    email: Yup.string().email("Invalid email").required("Required"),
  });

  // 2. Logic helpers
  const isPendingVerification =
    errorLoadingUserInfo?.toLowerCase().includes("pending") &&
    errorLoadingUserInfo?.toLowerCase().includes("verification");

  useEffect(() => {
    if (
      serverStatus?.operation === "resend-verification" &&
      serverStatus?.status === 200
    ) {
      setResendModalOpen(false);
      dispatch(clearUserInfoError());
      dispatch(resetAuthServerStatus());
    }
  }, [serverStatus, dispatch]);

  return (
    <Box sx={{ width: "100%" }}>
      <Formik
        initialValues={{ username: "", password: "" } as UserLoginDto}
        validationSchema={loginSchema}
        onSubmit={(values) => {
          let finalUsername = values.username;
          if (
            company?.companyCode &&
            !finalUsername.startsWith(`${company.companyCode}-`)
          ) {
            finalUsername = `${company.companyCode}-${values.username}`;
          }
          dispatch(
            loginUser(cleanFormData({ ...values, username: finalUsername })),
          );
        }}
      >
        {({ isValid, dirty }: FormikProps<UserLoginDto>) => (
          <Form>
            <Stack spacing={3}>
              {/* --- ALERTS --- */}
              {errorLoadingUserInfo && (
                <Alert
                  severity={isPendingVerification ? "warning" : "error"}
                  sx={{ textAlign: "left" }}
                  action={
                    isPendingVerification && (
                      <Button
                        color="inherit"
                        size="small"
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={() => setResendModalOpen(true)}
                        sx={{ fontWeight: 700 }}
                      >
                        Resend
                      </Button>
                    )
                  }
                >
                  <AlertTitle sx={{ fontWeight: 700 }}>
                    {isPendingVerification
                      ? "Account Not Active"
                      : "Login Failed"}
                  </AlertTitle>
                  <Typography sx={{ fontSize: "0.85rem" }}>
                    {errorLoadingUserInfo}
                  </Typography>
                </Alert>
              )}

              {/* --- USERNAME --- */}
              <FormTextField
                name="username"
                label="Username"
                placeholder={company ? "Admin" : "01-Admin"}
                slotProps={{
                  input: {
                    startAdornment: company ? (
                      <InputAdornment position="start">
                        <Typography
                          color="text.primary"
                          sx={{ fontWeight: "bold" }}
                        >
                          {company.companyCode} -
                        </Typography>
                      </InputAdornment>
                    ) : null,
                  },
                }}
              />

              {/* --- PASSWORD --- */}
              <Box>
                <FormTextField
                  name="password"
                  label="Password"
                  type="password"
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon color="action" />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}
                >
                  <Link
                    component={RouterLink}
                    to="/forgot-password"
                    sx={{
                      color: "primary.main",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Forgot password?
                  </Link>
                </Box>
              </Box>

              <SubmitButton
                title="Login"
                loadingText="Authenticating..."
                loading={loadingUserInfo}
                disabled={!dirty || !isValid || loadingUserInfo}
                endIcon={<LoginOutlinedIcon />}
                fullWidth
                sx={{ py: 1.5, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>

      {/* --- RESEND VERIFICATION MODAL --- */}
      <Dialog
        open={resendModalOpen}
        onClose={() => setResendModalOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: { sx: { borderRadius: 2, p: 1 } },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Resend Verification Link
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{ mb: 3, fontSize: "0.875rem", color: "text.secondary" }}
          >
            Enter your details below to receive a new verification link via
            email.
          </Typography>

          <Formik
            initialValues={{
              companyCode: company?.companyCode || "",
              email: "",
            }}
            validationSchema={resendSchema}
            enableReinitialize
            onSubmit={(values, { setSubmitting }) => {
              dispatch(resendVerificationEmail(cleanFormData(values))).finally(
                () => setSubmitting(false),
              );
            }}
          >
            {({
              isValid,
              isSubmitting,
            }: FormikProps<ResendVerificationValues>) => (
              <Form>
                <Stack spacing={2.5}>
                  <FormTextField
                    name="companyCode"
                    label="Company Code"
                    placeholder="e.g. 01"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                  <FormTextField
                    name="email"
                    label="Email Address"
                    type="email"
                    placeholder="name@example.com"
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                  <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="inherit"
                      onClick={() => setResendModalOpen(false)}
                      sx={{ fontWeight: 600 }}
                    >
                      Cancel
                    </Button>
                    <SubmitButton
                      loading={isSubmitting}
                      title="Send Link"
                      disabled={!isValid}
                      fullWidth
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                </Stack>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LoginForm;
